import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

const MarketingDashboard = () => {
  const { companyId, projectId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [customerSegments, setCustomerSegments] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Build params object, only include if values exist
        const params = {};
        if (companyId) params.company_id = companyId;
        if (projectId) params.project_id = projectId;
        
        console.log('Fetching marketing dashboard with params:', params);
        console.log('Token:', localStorage.getItem('token') ? 'present' : 'missing');
        console.log('Tenant:', localStorage.getItem('tenant'));
        
        const overviewResponse = await api.get('/marketing/analytics/overview', { params });
        console.log('Overview response:', overviewResponse.data);
        setOverview(overviewResponse.data);

        const segmentsResponse = await api.get('/marketing/customers/segments', { params });
        console.log('Segments response:', segmentsResponse.data);
        setCustomerSegments(segmentsResponse.data?.segments || []);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          request: error.request,
          code: error.code
        });
        const errorMessage = error.response?.data?.error || error.response?.data?.msg || error.message || 'Failed to load dashboard data';
        setError(`${errorMessage}. Please try again later.`);
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [companyId, projectId]);

  const formatCurrency = (amount) => {
    if (typeof amount !== 'number') return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatPercentage = (value) => {
    if (typeof value !== 'number') return '0%';
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div>Loading Marketing Dashboard...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <Link to="/dashboard" className="breadcrumb-link">Dashboard</Link>
        <span className="breadcrumb-separator">/</span>
        {companyId && (
          <>
            <Link to={`/companies/${companyId}/projects`} className="breadcrumb-link">Company</Link>
            <span className="breadcrumb-separator">/</span>
          </>
        )}
        {projectId && (
          <>
            <Link to={`/companies/${companyId}/projects/${projectId}/campaigns`} className="breadcrumb-link">Project</Link>
            <span className="breadcrumb-separator">/</span>
          </>
        )}
        <span className="breadcrumb-current">Marketing Hub</span>
      </nav>

      {/* Marketing Hub Card */}
      <div className="card">
        <div style={{
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <h2 style={{
            color: 'var(--gray-900)',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            margin: '0 0 0.25rem 0'
          }}>
            🤖 Marketeer Marketing Command Center
          </h2>
          <p style={{
            color: 'var(--gray-600)',
            margin: 0,
            fontSize: '0.875rem'
          }}>
            Complete AI-powered marketing automation platform
          </p>
        </div>

        {error && (
          <div style={{
            marginBottom: 'var(--space-6)',
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            borderRadius: '8px',
            padding: '0.75rem',
            color: 'var(--red-800)'
          }}>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}
        <div style={{
          display: 'flex',
          gap: 'var(--space-1)',
          borderBottom: '2px solid var(--gray-200)',
          marginBottom: 'var(--space-6)',
          overflowX: 'auto',
          flexWrap: 'wrap'
        }}>
          {[
            { key: 'overview', label: 'Overview', icon: '📊' },
            { key: 'campaigns', label: 'Campaigns', icon: '📢' },
            { key: 'email', label: 'Email Marketing', icon: '📧' },
            { key: 'social', label: 'Social Media', icon: '📱' },
            { key: 'seo', label: 'SEO & Content', icon: '🔍' },
            { key: 'paid-ads', label: 'Paid Advertising', icon: '💰' },
            { key: 'landing-pages', label: 'Landing Pages', icon: '🚀' },
            { key: 'analytics', label: 'Analytics', icon: '📈' },
            { key: 'vip', label: 'VIP Club', icon: '👑' },
            { key: 'segments', label: 'Customer Segments', icon: '🎯' },
            { key: 'automation', label: 'Automation', icon: '🤖' },
            { key: 'integrations', label: 'Integrations', icon: '🔗' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                border: 'none',
                backgroundColor: activeTab === tab.key ? 'var(--primary-50)' : 'transparent',
                color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === tab.key ? '2px solid var(--primary-600)' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: activeTab === tab.key ? 'var(--font-semibold)' : 'var(--font-medium)',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && overview && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              marginBottom: 'var(--space-5)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>📊 Platform Overview</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--space-6)'
            }}>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--primary-600)' }}>📧 Email Campaigns</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                  {overview.total_email_campaigns || 0}
                </div>
                <p style={{ color: 'var(--gray-600)', margin: '5px 0 0 0' }}>Active campaigns</p>
              </div>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--primary-600)' }}>📊 Performance</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                  {formatPercentage(overview.avg_campaign_performance || 0)}
                </div>
                <p style={{ color: 'var(--gray-600)', margin: '5px 0 0 0' }}>Engagement rate</p>
              </div>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--primary-600)' }}>💰 Revenue</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                  {formatCurrency(overview.total_revenue || 0)}
                </div>
                <p style={{ color: 'var(--gray-600)', margin: '5px 0 0 0' }}>Generated</p>
              </div>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--primary-600)' }}>🎯 Segments</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                  {overview.active_segments || 0}
                </div>
                <p style={{ color: 'var(--gray-600)', margin: '5px 0 0 0' }}>Active</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'segments' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-5)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>🎯 Customer Segmentation</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '15px'
            }}>
              {customerSegments.map((segment) => (
                <div
                  key={segment.segment}
                  style={{
                    background: 'var(--gray-50)',
                    padding: '20px',
                    borderRadius: '8px',
                    borderLeft: '4px solid var(--primary-600)'
                  }}
                >
                  <div style={{
                    fontWeight: 'bold',
                    color: 'var(--gray-900)',
                    marginBottom: '10px'
                  }}>{segment.segment?.replace('_', ' ') || 'Unknown'}</div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    fontSize: '0.9rem',
                    color: 'var(--gray-500)'
                  }}>
                    <div>Customers: {segment.count.toLocaleString()}</div>
                    <div>Avg. Spent: {formatCurrency(segment.avg_spent)}</div>
                    <div>Avg. Orders: {Math.round(segment.avg_orders)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'campaigns' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-5)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>📢 Marketing Campaigns</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 15px 0' }}>🚀 Active Campaigns</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: '10px' }}>12</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                  <span>Email: 8</span>
                  <span>Social: 3</span>
                  <span>Paid: 1</span>
                </div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 15px 0' }}>📊 This Month</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: '10px' }}>$24,580</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>↗ +18% from last month</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 15px 0' }}>🎯 Avg. CTR</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: '10px' }}>3.2%</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>Industry avg: 2.1%</div>
              </div>
            </div>

            <div className="card">
              <h3 style={{ margin: '0 0 20px 0', color: 'var(--gray-900)' }}>Recent Campaign Activity</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {[
                  { name: 'Holiday Sale Email Campaign', status: 'Active', performance: '+24%', type: 'Email' },
                  { name: 'Social Media Brand Awareness', status: 'Running', performance: '+12%', type: 'Social' },
                  { name: 'Google Ads - Product Launch', status: 'Optimizing', performance: '+8%', type: 'Paid Ads' },
                  { name: 'Newsletter Subscriber Drive', status: 'Completed', performance: '+31%', type: 'Email' }
                ].map((campaign, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                  }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: 'var(--gray-900)' }}>{campaign.name}</div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--gray-600)' }}>{campaign.type}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '0.8rem',
                        padding: '4px 8px',
                        borderRadius: '8px',
                        backgroundColor: campaign.status === 'Active' || campaign.status === 'Running' ? 'var(--green-100)' :
                          campaign.status === 'Completed' ? 'var(--blue-100)' : 'var(--yellow-100)',
                        color: campaign.status === 'Active' || campaign.status === 'Running' ? 'var(--green-800)' :
                          campaign.status === 'Completed' ? 'var(--blue-800)' : 'var(--yellow-800)',
                        marginBottom: '4px'
                      }}>
                        {campaign.status}
                      </div>
                      <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'var(--green-600)' }}>
                        {campaign.performance}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-5)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>📧 Email Marketing</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📮 Subscribers</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>15,847</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+127 this week</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📬 Open Rate</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>24.8%</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>Above industry avg</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>🎯 Click Rate</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>4.2%</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+0.8% from last month</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>💰 Revenue</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>$18,240</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>This month</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>📊 Recent Campaigns</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { name: 'Weekly Newsletter #47', sent: '2,847', opened: '24.3%', clicked: '4.1%' },
                    { name: 'Black Friday Special', sent: '15,203', opened: '31.2%', clicked: '8.7%' },
                    { name: 'Product Update Announcement', sent: '12,891', opened: '19.8%', clicked: '3.2%' }
                  ].map((campaign, index) => (
                    <div key={index} style={{
                      padding: '12px 0',
                      borderBottom: index < 2 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div style={{ fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: '4px' }}>
                        {campaign.name}
                      </div>
                      <div style={{ display: 'flex', gap: '15px', fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                        <span>Sent: {campaign.sent}</span>
                        <span>Opened: {campaign.opened}</span>
                        <span>Clicked: {campaign.clicked}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>🎯 Top Performing Content</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { subject: '"Don\'t miss out - 48hrs left!"', rate: '31.2%', type: 'Promotional' },
                    { subject: '"Your weekly industry insights"', rate: '28.7%', type: 'Newsletter' },
                    { subject: '"New feature announcement"', rate: '24.1%', type: 'Product Update' },
                    { subject: '"Welcome to our community"', rate: '22.8%', type: 'Welcome Series' }
                  ].map((email, index) => (
                    <div key={index} style={{
                      padding: '10px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div style={{ fontWeight: 'bold', color: 'var(--gray-900)', fontSize: '0.95rem', marginBottom: '4px' }}>
                        {email.subject}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span className="breadcrumb-current">{email.type}</span>
                        <span style={{ color: 'var(--green-600)', fontWeight: 'bold' }}>{email.rate} open rate</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'social' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-5)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>📱 Social Media Marketing</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>👥 Total Followers</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>47,239</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+1,847 this month</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>❤️ Engagement Rate</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>5.8%</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>Above industry avg</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📊 Reach</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>124K</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+18% this week</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>🔗 Click-through</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>3.2%</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+0.7% improvement</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>🏆 Platform Performance</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {[
                    { platform: 'LinkedIn', icon: '💼', followers: '15,247', engagement: '7.2%', growth: '+12.3%' },
                    { platform: 'Twitter/X', icon: '🐦', followers: '18,392', engagement: '4.9%', growth: '+8.7%' },
                    { platform: 'Instagram', icon: '📸', followers: '8,934', engagement: '6.1%', growth: '+15.2%' },
                    { platform: 'Facebook', icon: '📘', followers: '4,666', engagement: '3.8%', growth: '+4.1%' }
                  ].map((platform, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.2rem' }}>{platform.icon}</span>
                        <div>
                          <div style={{ fontWeight: 'bold', color: 'var(--gray-900)' }}>{platform.platform}</div>
                          <div style={{ fontSize: '0.9rem', color: 'var(--gray-600)' }}>
                            {platform.followers} followers
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                          {platform.engagement}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--green-600)' }}>
                          {platform.growth}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>🔥 Top Performing Posts</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { content: 'AI Marketing trends for 2025...', platform: '💼', likes: '347', comments: '28', shares: '45' },
                    { content: 'Behind the scenes: Our team...', platform: '📸', likes: '523', comments: '41', shares: '23' },
                    { content: 'Quick tip: Email subject lines...', platform: '🐦', likes: '289', comments: '19', shares: '67' },
                    { content: 'Customer success story...', platform: '📘', likes: '198', comments: '15', shares: '12' }
                  ].map((post, index) => (
                    <div key={index} style={{
                      padding: '12px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '6px'
                      }}>
                        <span>{post.platform}</span>
                        <div style={{
                          fontWeight: 'bold',
                          color: 'var(--gray-900)',
                          fontSize: '0.9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {post.content}
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        fontSize: '0.8rem',
                        color: 'var(--gray-600)'
                      }}>
                        <span>❤️ {post.likes}</span>
                        <span>💬 {post.comments}</span>
                        <span>🔄 {post.shares}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'seo' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-5)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>🔍 SEO & Content Strategy</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📊 Domain Authority</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>68/100</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+3 points this quarter</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>🎯 Ranking Keywords</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>1,247</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+89 new rankings</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📈 Organic Traffic</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>11,240</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+14.2% this month</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📝 Content Pieces</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>147</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--blue-600)' }}>12 published this month</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>🏆 Top Performing Keywords</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { keyword: 'AI marketing automation', position: '#3', traffic: '2,847', trend: '↗ +2' },
                    { keyword: 'email marketing platform', position: '#7', traffic: '1,920', trend: '↗ +5' },
                    { keyword: 'customer segmentation', position: '#12', traffic: '1,456', trend: '↗ +3' },
                    { keyword: 'marketing analytics dashboard', position: '#15', traffic: '1,123', trend: '→ 0' }
                  ].map((keyword, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: 'var(--gray-900)', fontSize: '0.95rem' }}>
                          {keyword.keyword}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                          Position {keyword.position} • {keyword.traffic} monthly visits
                        </div>
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: keyword.trend.includes('↗') ? 'var(--green-600)' : 'var(--gray-600)',
                        fontWeight: 'bold'
                      }}>
                        {keyword.trend}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>📰 Recent Content Performance</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { title: 'Complete Guide to AI Marketing', views: '4,247', engagement: '8.2%' },
                    { title: 'Email Automation Best Practices', views: '3,891', engagement: '7.8%' },
                    { title: '2025 Marketing Trends Report', views: '5,123', engagement: '9.1%' },
                    { title: 'Customer Journey Mapping Tutorial', views: '2,847', engagement: '6.9%' }
                  ].map((content, index) => (
                    <div key={index} style={{
                      padding: '12px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div style={{ fontWeight: 'bold', color: 'var(--gray-900)', fontSize: '0.95rem', marginBottom: '4px' }}>
                        {content.title}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span className="breadcrumb-current">{content.views} views</span>
                        <span style={{ color: 'var(--green-600)', fontWeight: 'bold' }}>
                          {content.engagement} engagement
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'paid-ads' && (
          <div>
            <h2>💰 Paid Advertising</h2>
            <p>Paid advertising management coming soon...</p>
          </div>
        )}

        {activeTab === 'landing-pages' && (
          <div>
            <h2>🚀 Landing Pages</h2>
            <p>Landing page tools coming soon...</p>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-5)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>📈 Marketing Analytics</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>👥 Total Visitors</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>28,547</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>↗ +12.3% vs last month</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>🔄 Conversion Rate</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>3.84%</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>↗ +0.47% improvement</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>💰 Revenue</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>$52,180</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>+$8,240 this month</div>
              </div>

              <div className="card">
                <h3 style={{ color: 'var(--primary-600)', margin: '0 0 10px 0' }}>📊 ROAS</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>4.2x</div>
                <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>Return on ad spend</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>🎯 Top Traffic Sources</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { source: 'Organic Search', visitors: '11,240', percentage: '39.4%', change: '+5.2%' },
                    { source: 'Social Media', visitors: '8,547', percentage: '29.9%', change: '+12.8%' },
                    { source: 'Email Marketing', visitors: '4,821', percentage: '16.9%', change: '+8.1%' },
                    { source: 'Paid Advertising', visitors: '3,939', percentage: '13.8%', change: '+15.3%' }
                  ].map((source, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: 'var(--gray-900)' }}>{source.source}</div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--gray-600)' }}>{source.visitors} visitors</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 'bold', color: 'var(--gray-900)' }}>{source.percentage}</div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--green-600)' }}>{source.change}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card">
                <h3 style={{ margin: '0 0 15px 0', color: 'var(--gray-900)' }}>🏆 Best Performing Campaigns</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { name: 'Holiday Email Series', roi: '340%', revenue: '$12,450' },
                    { name: 'Social Media Ads - Q4', roi: '285%', revenue: '$9,120' },
                    { name: 'Google Ads - Brand', roi: '220%', revenue: '$7,890' },
                    { name: 'LinkedIn B2B Campaign', roi: '195%', revenue: '$6,340' }
                  ].map((campaign, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: index < 3 ? '1px solid var(--gray-200)' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: 'var(--gray-900)', fontSize: '0.95rem' }}>
                          {campaign.name}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--gray-600)' }}>{campaign.revenue}</div>
                      </div>
                      <div style={{
                        padding: '4px 8px',
                        borderRadius: '8px',
                        backgroundColor: 'var(--green-100)',
                        color: 'var(--green-800)',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        {campaign.roi} ROI
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'vip' && (
          <div>
            <h2>👑 VIP Club</h2>
            <p>VIP customer management coming soon...</p>
          </div>
        )}

        {activeTab === 'automation' && (
          <div>
            <h2>🤖 Automation</h2>
            <p>Marketing automation coming soon...</p>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div>
            <h2>🔗 Integrations</h2>
            <p>Integration management coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingDashboard;
